import { create } from "zustand";

import {
  captureAuthScope,
  isCurrentAuthScope,
  type AuthScopeToken,
} from "@/lib/auth/authScopeGeneration";
import type {
  CNSPluginCatalogPage,
  CNSPluginDetail,
  CNSPluginListQuery,
  CNSWorkspacePluginCatalogPage,
  CNSWorkspacePluginDetail,
  CNSWorkspacePluginListQuery,
  CNSWorkspacePluginSummary,
} from "@/models/plugin";
import {
  getPlugin as getPluginRequest,
  getWorkspacePlugin as getWorkspacePluginRequest,
  listPlugins as listPluginsRequest,
  listWorkspacePlugins as listWorkspacePluginsRequest,
} from "@/services/pluginService";

export const EMPTY_PLUGIN_CATALOG_PAGE: CNSPluginCatalogPage = {
  content: [],
  categories: [],
  catalogRevision: "",
  totalElements: 0,
  totalPages: 0,
  number: 0,
  numberOfElements: 0,
  first: true,
  last: true,
};

export const EMPTY_WORKSPACE_PLUGIN_CATALOG_PAGE: CNSWorkspacePluginCatalogPage = {
  content: [],
  categories: [],
  catalogRevision: "",
  totalElements: 0,
  totalPages: 0,
  number: 0,
  numberOfElements: 0,
  first: true,
  last: true,
};

export type PluginResourceKind =
  | "catalog"
  | "catalogDetail"
  | "workspaceList"
  | "workspaceDetail";

export interface PluginCatalogListKey extends CNSPluginListQuery {
  kind: "catalog";
}

export interface PluginCatalogDetailKey {
  kind: "catalogDetail";
  pluginKey: string;
}

export interface PluginWorkspaceListKey extends CNSWorkspacePluginListQuery {
  kind: "workspaceList";
  workspaceId: string;
}

export interface PluginWorkspaceDetailKey {
  kind: "workspaceDetail";
  workspaceId: string;
  projectId?: string | null;
  pluginKey: string;
}

export type PluginCacheKey =
  | PluginCatalogListKey
  | PluginCatalogDetailKey
  | PluginWorkspaceListKey
  | PluginWorkspaceDetailKey;

type PluginCatalogEntry = {
  data: CNSPluginCatalogPage;
  authScope: AuthScopeToken | null;
};

type PluginCatalogDetailEntry = {
  data: CNSPluginDetail;
  authScope: AuthScopeToken | null;
  requestedPluginKey: string;
};

type PluginWorkspaceListEntry = {
  data: CNSWorkspacePluginCatalogPage;
  authScope: AuthScopeToken | null;
};

type PluginWorkspaceDetailEntry = {
  data: CNSWorkspacePluginDetail;
  authScope: AuthScopeToken | null;
  requestedPluginKey: string;
};

type PluginCacheEntry =
  | PluginCatalogEntry
  | PluginCatalogDetailEntry
  | PluginWorkspaceListEntry
  | PluginWorkspaceDetailEntry;

function normalizeSortArray(sort: string[] | undefined): string[] {
  return [...(sort ?? [])].sort();
}

function readSortFromParsed(parsed: Record<string, unknown>): string[] | undefined {
  if (Array.isArray(parsed.sort)) {
    const values = parsed.sort.filter(
      (value): value is string => typeof value === "string",
    );
    return values.length > 0 ? normalizeSortArray(values) : undefined;
  }
  if (typeof parsed.sort === "string" && parsed.sort.length > 0) {
    // Legacy cache keys stored a single joined sort string.
    return [parsed.sort];
  }
  return undefined;
}

function normalizeText(value: string | undefined): string {
  return value?.trim() ?? "";
}

/** Builds a stable cache key from the complete normalized request identity. */
export function buildPluginCacheKey(key: PluginCacheKey): string {
  switch (key.kind) {
    case "catalog":
      return JSON.stringify({
        kind: key.kind,
        page: key.page ?? 0,
        size: key.size ?? 20,
        sort: normalizeSortArray(key.sort),
        query: normalizeText(key.query),
        category: normalizeText(key.category),
      });
    case "catalogDetail":
      return JSON.stringify({
        kind: key.kind,
        pluginKey: key.pluginKey,
      });
    case "workspaceList":
      return JSON.stringify({
        kind: key.kind,
        workspaceId: key.workspaceId,
        projectId: key.projectId ?? null,
        scope: key.scope ?? "all",
        page: key.page ?? 0,
        size: key.size ?? 20,
        sort: normalizeSortArray(key.sort),
        query: normalizeText(key.query),
        category: normalizeText(key.category),
      });
    case "workspaceDetail":
      return JSON.stringify({
        kind: key.kind,
        workspaceId: key.workspaceId,
        projectId: key.projectId ?? null,
        pluginKey: key.pluginKey,
      });
  }
}

function buildAliasMapKey(
  authScope: AuthScopeToken | null,
  catalogRevision: string,
  requestedPluginKey: string,
): string | null {
  if (!authScope) return null;
  return `${authScope.authScopeKey}:${authScope.generation}:${catalogRevision}:${requestedPluginKey}`;
}

const inflight = new Map<string, Promise<unknown>>();
const actionClaims = new Set<string>();

interface PluginStore {
  authScopeKey: string | null;
  entriesByKey: Record<string, PluginCacheEntry>;
  aliasToCanonicalByKey: Record<string, string>;
  loadingByKey: Record<string, boolean>;
  errorByKey: Record<string, string | null>;
  setAuthScopeKey: (authScopeKey: string | null) => void;
  fetchCatalog: (
    query?: CNSPluginListQuery,
    options?: { force?: boolean },
  ) => Promise<CNSPluginCatalogPage>;
  fetchCatalogDetail: (
    pluginKey: string,
    options?: { force?: boolean },
  ) => Promise<CNSPluginDetail>;
  fetchWorkspaceList: (
    workspaceId: string,
    query?: CNSWorkspacePluginListQuery,
    options?: { force?: boolean },
  ) => Promise<CNSWorkspacePluginCatalogPage>;
  fetchWorkspaceDetail: (
    workspaceId: string,
    pluginKey: string,
    projectId?: string | null,
    options?: { force?: boolean },
  ) => Promise<CNSWorkspacePluginDetail>;
  resolveCanonicalPluginKey: (
    requestedPluginKey: string,
    catalogRevision?: string | null,
  ) => string;
  claimAction: (actionKey: string) => boolean;
  releaseAction: (actionKey: string) => void;
  invalidateWorkspace: (workspaceId: string, projectId?: string | null) => void;
  invalidateCatalog: () => void;
  reset: () => void;
}

function getCurrentAuthScope(get: () => PluginStore): AuthScopeToken | null {
  return captureAuthScope(get().authScopeKey);
}

function shouldStoreResult(
  authScope: AuthScopeToken | null,
  captured: AuthScopeToken | null,
): boolean {
  if (!captured) return true;
  return isCurrentAuthScope(captured) && authScope?.authScopeKey === captured.authScopeKey;
}

async function runSingleFlight<T>(
  cacheKey: string,
  runner: () => Promise<T>,
  options?: { force?: boolean },
): Promise<T> {
  const existing = inflight.get(cacheKey);
  if (existing) {
    if (!options?.force) return existing as Promise<T>;
    await existing.catch(() => undefined);
    if (inflight.get(cacheKey) === existing) {
      inflight.delete(cacheKey);
    }
  }
  const request = runner().finally(() => {
    if (inflight.get(cacheKey) === request) {
      inflight.delete(cacheKey);
    }
  });
  inflight.set(cacheKey, request);
  return request;
}

/** Reconstructs a cache key object from its stable JSON string identity. */
function parsePluginCacheKey(cacheKey: string): PluginCacheKey | null {
  try {
    const parsed = JSON.parse(cacheKey) as Record<string, unknown>;
    const kind = parsed.kind;
    if (kind === "catalog") {
      return {
        kind: "catalog",
        page: typeof parsed.page === "number" ? parsed.page : undefined,
        size: typeof parsed.size === "number" ? parsed.size : undefined,
        sort: readSortFromParsed(parsed),
        query: typeof parsed.query === "string" ? parsed.query : undefined,
        category: typeof parsed.category === "string" ? parsed.category : undefined,
      };
    }
    if (kind === "catalogDetail" && typeof parsed.pluginKey === "string") {
      return { kind: "catalogDetail", pluginKey: parsed.pluginKey };
    }
    if (kind === "workspaceList" && typeof parsed.workspaceId === "string") {
      return {
        kind: "workspaceList",
        workspaceId: parsed.workspaceId,
        projectId:
          typeof parsed.projectId === "string"
            ? parsed.projectId
            : parsed.projectId === null
              ? null
              : undefined,
        scope:
          parsed.scope === "all" || parsed.scope === "yours"
            ? parsed.scope
            : undefined,
        page: typeof parsed.page === "number" ? parsed.page : undefined,
        size: typeof parsed.size === "number" ? parsed.size : undefined,
        sort: readSortFromParsed(parsed),
        query: typeof parsed.query === "string" ? parsed.query : undefined,
        category: typeof parsed.category === "string" ? parsed.category : undefined,
      };
    }
    if (
      kind === "workspaceDetail" &&
      typeof parsed.workspaceId === "string" &&
      typeof parsed.pluginKey === "string"
    ) {
      return {
        kind: "workspaceDetail",
        workspaceId: parsed.workspaceId,
        pluginKey: parsed.pluginKey,
        projectId:
          typeof parsed.projectId === "string"
            ? parsed.projectId
            : parsed.projectId === null
              ? null
              : undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function matchesWorkspaceInvalidation(
  key: PluginWorkspaceListKey | PluginWorkspaceDetailKey,
  workspaceId: string,
  projectId?: string | null,
): boolean {
  if (key.workspaceId !== workspaceId) return false;
  if (!projectId) return true;
  if (!key.projectId) return true;
  return key.projectId === projectId;
}

function refetchPluginCacheKey(store: PluginStore, key: PluginCacheKey): void {
  switch (key.kind) {
    case "catalog": {
      const { kind: _kind, ...query } = key;
      void store.fetchCatalog(query, { force: true });
      return;
    }
    case "catalogDetail":
      void store.fetchCatalogDetail(key.pluginKey, { force: true });
      return;
    case "workspaceList": {
      const { kind: _kind, workspaceId, ...query } = key;
      void store.fetchWorkspaceList(workspaceId, query, { force: true });
      return;
    }
    case "workspaceDetail":
      void store.fetchWorkspaceDetail(
        key.workspaceId,
        key.pluginKey,
        key.projectId,
        { force: true },
      );
  }
}

export const usePluginStore = create<PluginStore>((set, get) => ({
  authScopeKey: null,
  entriesByKey: {},
  aliasToCanonicalByKey: {},
  loadingByKey: {},
  errorByKey: {},

  setAuthScopeKey: (authScopeKey) => {
    set({ authScopeKey });
  },

  fetchCatalog: async (query = {}, options) => {
    const key: PluginCatalogListKey = { kind: "catalog", ...query };
    const cacheKey = buildPluginCacheKey(key);
    const cached = get().entriesByKey[cacheKey];
    if (cached && cached.authScope && isCurrentAuthScope(cached.authScope) && !options?.force) {
      return (cached as PluginCatalogEntry).data;
    }

    const captured = getCurrentAuthScope(get);
    return runSingleFlight(cacheKey, async () => {
      set((state) => ({
        loadingByKey: { ...state.loadingByKey, [cacheKey]: true },
        errorByKey: { ...state.errorByKey, [cacheKey]: null },
      }));
      try {
        const data = await listPluginsRequest(query);
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            entriesByKey: {
              ...state.entriesByKey,
              [cacheKey]: { data, authScope: captured },
            },
          }));
        }
        return data;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to load plugins";
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            errorByKey: { ...state.errorByKey, [cacheKey]: message },
          }));
        }
        throw error;
      } finally {
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            loadingByKey: { ...state.loadingByKey, [cacheKey]: false },
          }));
        }
      }
    }, { force: options?.force });
  },

  fetchCatalogDetail: async (pluginKey, options) => {
    const requestedPluginKey = pluginKey;
    const key: PluginCatalogDetailKey = { kind: "catalogDetail", pluginKey: requestedPluginKey };
    const cacheKey = buildPluginCacheKey(key);
    const cached = get().entriesByKey[cacheKey];
    if (cached && cached.authScope && isCurrentAuthScope(cached.authScope) && !options?.force) {
      return (cached as PluginCatalogDetailEntry).data;
    }

    const captured = getCurrentAuthScope(get);
    return runSingleFlight(cacheKey, async () => {
      set((state) => ({
        loadingByKey: { ...state.loadingByKey, [cacheKey]: true },
        errorByKey: { ...state.errorByKey, [cacheKey]: null },
      }));
      try {
        const data = await getPluginRequest(requestedPluginKey);
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          const canonicalKey = data.pluginKey;
          const canonicalCacheKey = buildPluginCacheKey({
            kind: "catalogDetail",
            pluginKey: canonicalKey,
          });
          const aliasMapKey = buildAliasMapKey(
            captured,
            "",
            requestedPluginKey,
          );
          set((state) => {
            const nextEntries = { ...state.entriesByKey };
            delete nextEntries[cacheKey];
            nextEntries[canonicalCacheKey] = {
              data,
              authScope: captured,
              requestedPluginKey,
            };
            const aliasToCanonicalByKey = { ...state.aliasToCanonicalByKey };
            if (aliasMapKey && requestedPluginKey !== canonicalKey) {
              aliasToCanonicalByKey[aliasMapKey] = canonicalKey;
            }
            return { entriesByKey: nextEntries, aliasToCanonicalByKey };
          });
        }
        return data;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to load plugin";
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            errorByKey: { ...state.errorByKey, [cacheKey]: message },
          }));
        }
        throw error;
      } finally {
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            loadingByKey: { ...state.loadingByKey, [cacheKey]: false },
          }));
        }
      }
    }, { force: options?.force });
  },

  fetchWorkspaceList: async (workspaceId, query = {}, options) => {
    const key: PluginWorkspaceListKey = {
      kind: "workspaceList",
      workspaceId,
      ...query,
    };
    const cacheKey = buildPluginCacheKey(key);
    const cached = get().entriesByKey[cacheKey];
    if (cached && cached.authScope && isCurrentAuthScope(cached.authScope) && !options?.force) {
      return (cached as PluginWorkspaceListEntry).data;
    }

    const captured = getCurrentAuthScope(get);
    return runSingleFlight(cacheKey, async () => {
      set((state) => ({
        loadingByKey: { ...state.loadingByKey, [cacheKey]: true },
        errorByKey: { ...state.errorByKey, [cacheKey]: null },
      }));
      try {
        const data = await listWorkspacePluginsRequest(workspaceId, query);
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            entriesByKey: {
              ...state.entriesByKey,
              [cacheKey]: { data, authScope: captured },
            },
          }));
        }
        return data;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to load workspace plugins";
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            errorByKey: { ...state.errorByKey, [cacheKey]: message },
          }));
        }
        throw error;
      } finally {
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            loadingByKey: { ...state.loadingByKey, [cacheKey]: false },
          }));
        }
      }
    }, { force: options?.force });
  },

  fetchWorkspaceDetail: async (workspaceId, pluginKey, projectId, options) => {
    const requestedPluginKey = pluginKey;
    const key: PluginWorkspaceDetailKey = {
      kind: "workspaceDetail",
      workspaceId,
      projectId: projectId ?? null,
      pluginKey: requestedPluginKey,
    };
    const cacheKey = buildPluginCacheKey(key);
    const cached = get().entriesByKey[cacheKey];
    if (cached && cached.authScope && isCurrentAuthScope(cached.authScope) && !options?.force) {
      return (cached as PluginWorkspaceDetailEntry).data;
    }

    const captured = getCurrentAuthScope(get);
    return runSingleFlight(cacheKey, async () => {
      set((state) => ({
        loadingByKey: { ...state.loadingByKey, [cacheKey]: true },
        errorByKey: { ...state.errorByKey, [cacheKey]: null },
      }));
      try {
        const data = await getWorkspacePluginRequest(
          workspaceId,
          requestedPluginKey,
          projectId,
        );
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          const canonicalKey = data.definition.pluginKey;
          const canonicalCacheKey = buildPluginCacheKey({
            kind: "workspaceDetail",
            workspaceId,
            projectId: projectId ?? null,
            pluginKey: canonicalKey,
          });
          const aliasMapKey = buildAliasMapKey(
            captured,
            "",
            requestedPluginKey,
          );
          set((state) => {
            const nextEntries = { ...state.entriesByKey };
            delete nextEntries[cacheKey];
            nextEntries[canonicalCacheKey] = {
              data,
              authScope: captured,
              requestedPluginKey,
            };
            const aliasToCanonicalByKey = { ...state.aliasToCanonicalByKey };
            if (aliasMapKey && requestedPluginKey !== canonicalKey) {
              aliasToCanonicalByKey[aliasMapKey] = canonicalKey;
            }
            return { entriesByKey: nextEntries, aliasToCanonicalByKey };
          });
        }
        return data;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to load workspace plugin";
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            errorByKey: { ...state.errorByKey, [cacheKey]: message },
          }));
        }
        throw error;
      } finally {
        if (shouldStoreResult(get().authScopeKey ? captured : null, captured)) {
          set((state) => ({
            loadingByKey: { ...state.loadingByKey, [cacheKey]: false },
          }));
        }
      }
    }, { force: options?.force });
  },

  resolveCanonicalPluginKey: (requestedPluginKey, catalogRevision) => {
    const captured = getCurrentAuthScope(get);
    const aliasMapKey = buildAliasMapKey(captured, catalogRevision ?? "", requestedPluginKey);
    if (!aliasMapKey) return requestedPluginKey;
    return get().aliasToCanonicalByKey[aliasMapKey] ?? requestedPluginKey;
  },

  claimAction: (actionKey) => {
    if (actionClaims.has(actionKey)) return false;
    actionClaims.add(actionKey);
    return true;
  },

  releaseAction: (actionKey) => {
    actionClaims.delete(actionKey);
  },

  invalidateWorkspace: (workspaceId, projectId) => {
    const keysToRefetch: PluginCacheKey[] = [];
    for (const cacheKey of Object.keys(get().entriesByKey)) {
      const parsed = parsePluginCacheKey(cacheKey);
      if (!parsed) continue;
      if (parsed.kind !== "workspaceList" && parsed.kind !== "workspaceDetail") continue;
      if (!matchesWorkspaceInvalidation(parsed, workspaceId, projectId)) continue;
      keysToRefetch.push(parsed);
    }

    set((state) => {
      const nextEntries = { ...state.entriesByKey };
      const nextLoading = { ...state.loadingByKey };
      const nextErrors = { ...state.errorByKey };
      for (const key of keysToRefetch.map((entry) => buildPluginCacheKey(entry))) {
        delete nextEntries[key];
        delete nextLoading[key];
        delete nextErrors[key];
      }
      return {
        entriesByKey: nextEntries,
        loadingByKey: nextLoading,
        errorByKey: nextErrors,
      };
    });

    const store = get();
    for (const key of keysToRefetch) {
      refetchPluginCacheKey(store, key);
    }
  },

  invalidateCatalog: () => {
    const keysToRefetch: PluginCacheKey[] = [];
    for (const cacheKey of Object.keys(get().entriesByKey)) {
      const parsed = parsePluginCacheKey(cacheKey);
      if (!parsed) continue;
      if (parsed.kind !== "catalog" && parsed.kind !== "catalogDetail") continue;
      keysToRefetch.push(parsed);
    }

    set((state) => {
      const nextEntries = { ...state.entriesByKey };
      const nextLoading = { ...state.loadingByKey };
      const nextErrors = { ...state.errorByKey };
      for (const key of keysToRefetch.map((entry) => buildPluginCacheKey(entry))) {
        delete nextEntries[key];
        delete nextLoading[key];
        delete nextErrors[key];
      }
      return {
        entriesByKey: nextEntries,
        loadingByKey: nextLoading,
        errorByKey: nextErrors,
        aliasToCanonicalByKey: {},
      };
    });

    const store = get();
    for (const key of keysToRefetch) {
      refetchPluginCacheKey(store, key);
    }
  },

  reset: () => {
    inflight.clear();
    actionClaims.clear();
    set({
      entriesByKey: {},
      aliasToCanonicalByKey: {},
      loadingByKey: {},
      errorByKey: {},
    });
  },
}));

/** Selects a cached catalog page for the given query key. */
export function selectPluginCatalogPage(
  state: PluginStore,
  query: CNSPluginListQuery = {},
): CNSPluginCatalogPage {
  const cacheKey = buildPluginCacheKey({ kind: "catalog", ...query });
  const entry = state.entriesByKey[cacheKey];
  return entry && "data" in entry && Array.isArray((entry.data as CNSPluginCatalogPage).content)
    ? (entry as PluginCatalogEntry).data
    : EMPTY_PLUGIN_CATALOG_PAGE;
}

/** Selects a cached workspace plugin page for the given query key. */
export function selectWorkspacePluginPage(
  state: PluginStore,
  workspaceId: string,
  query: CNSWorkspacePluginListQuery = {},
): CNSWorkspacePluginCatalogPage {
  const cacheKey = buildPluginCacheKey({ kind: "workspaceList", workspaceId, ...query });
  const entry = state.entriesByKey[cacheKey];
  return entry && "data" in entry && Array.isArray((entry.data as CNSWorkspacePluginCatalogPage).content)
    ? (entry as PluginWorkspaceListEntry).data
    : EMPTY_WORKSPACE_PLUGIN_CATALOG_PAGE;
}

/** Returns whether the store is loading a specific cache key. */
export function selectPluginLoading(
  state: PluginStore,
  key: PluginCacheKey,
): boolean {
  return state.loadingByKey[buildPluginCacheKey(key)] ?? false;
}

/** Selects a cached workspace plugin detail entry. */
export function selectWorkspacePluginDetail(
  state: PluginStore,
  key: PluginWorkspaceDetailKey,
): CNSWorkspacePluginDetail | null {
  const cacheKey = buildPluginCacheKey(key);
  const entry = state.entriesByKey[cacheKey];
  if (!entry || !("requestedPluginKey" in entry)) return null;
  return (entry as PluginWorkspaceDetailEntry).data;
}

/** Selects workspace plugin summaries from a cached list entry. */
export function selectWorkspacePluginSummaries(
  state: PluginStore,
  workspaceId: string,
  query: CNSWorkspacePluginListQuery = {},
): CNSWorkspacePluginSummary[] {
  const page = selectWorkspacePluginPage(state, workspaceId, query);
  return page.content as CNSWorkspacePluginSummary[];
}
/** Returns the cached error for a specific cache key. */
export function selectPluginError(
  state: PluginStore,
  key: PluginCacheKey,
): string | null {
  return state.errorByKey[buildPluginCacheKey(key)] ?? null;
}
